import { useHandleHttpError } from '../../../lib/hooks';
import { BulmaType, ButtonProps, DatePicker, Form, Input, ISelectOption, Select, TaskPanel } from '@easylife365/react';
import type { AddPasswordModalProps } from './types';
import { ChangeEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faCheck, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import * as Yup from 'yup';
import { AddPasswordRequest } from '../../../api/app.types';
import { useAddPassword } from '../../../api/app.api';
import { Formik } from 'formik';
import { useAppDetailContext } from '../../../lib/detail';

const defaultExpiration = computeExpirationDate(180);

const MAX_YEARS = 5;

const minDate = new Date();
minDate.setUTCHours(23, 59, 59, 0);

const maxDate = new Date();
maxDate.setFullYear(maxDate.getFullYear() + MAX_YEARS);
maxDate.setUTCHours(23, 59, 59, 0);

const AddPasswordPanel: React.FC<AddPasswordModalProps> = ({ isOpen, close }) => {
  const { t } = useTranslation('addPasswordPanel');
  const { t: tCommon } = useTranslation('common');
  const { app } = useAppDetailContext();
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        endDateTime: Yup.date()
          .required(tCommon('validation.expirationRequired'))
          .min(minDate, tCommon('validation.expirationPast'))
          .max(maxDate, tCommon('validation.expirationMax', { years: MAX_YEARS })),
      }),
    [tCommon]
  );
  const expirationOptions = useMemo<ISelectOption[]>(
    () => [
      { key: defaultExpiration, text: t('expirationOptions.recommended') },
      { key: computeExpirationDate(90), text: t('expirationOptions.days90') },
      { key: computeExpirationDate(365), text: t('expirationOptions.days365') },
      { key: computeExpirationDate(545), text: t('expirationOptions.days545') },
      { key: computeExpirationDate(730), text: t('expirationOptions.days730') },
      { key: '-1', text: t('expirationOptions.custom') },
    ],
    [t]
  );
  const handleHttpError = useHandleHttpError();
  const [selectedOption, setSelectedOption] = useState<string>(defaultExpiration);
  const { data: addPasswordResponse, mutateAsync: addPassword, reset: resetAddPassword, isPending } = useAddPassword();

  const onSubmit = async (values: AddPasswordRequest) => {
    try {
      await addPassword({
        id: app.id,
        request: values,
      });
    } catch (error) {
      handleHttpError(error);
    }
  };

  const title = addPasswordResponse ? t('title.created') : t('title.generate');

  const initialValue: AddPasswordRequest = {
    appType: app.resourceType,
    endDateTime: new Date(defaultExpiration),
  };

  return (
    <Formik
      initialValues={initialValue}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnMount
    >
      {({ setFieldValue, isSubmitting, isValid, handleSubmit, resetForm }) => {
        // The panel stays mounted while hidden, so clear the created secret and the form on close;
        // otherwise the next open shows the previous secret instead of a fresh form.
        const handleModalClose = () => {
          // Closing mid-request would detach the result, leaving a secret created in Entra that nobody saw.
          // isPending covers the moment after Formik finishes submitting but before the result renders.
          if (isSubmitting || isPending) return;

          close();
          setSelectedOption(defaultExpiration);
          resetForm();
          resetAddPassword();
        };

        const onExpirationChange = (e: ChangeEvent<HTMLSelectElement>) => {
          const newValue = e.target.value;
          setSelectedOption(newValue);

          if (newValue !== '-1') {
            setFieldValue('endDateTime', newValue);
          }
        };

        const ctaButtons: ButtonProps[] = !addPasswordResponse
          ? [
              {
                bulmaType: BulmaType.Primary,
                disabled: !isValid || isSubmitting || isPending,
                text: t('buttons.generate'),
                icon: faWandMagicSparkles,
                loading: isSubmitting,
                type: 'submit',
              },
            ]
          : [
              {
                bulmaType: BulmaType.Primary,
                text: tCommon('actions.done'),
                icon: faCheck,
                onClick: handleModalClose,
              },
            ];

        return (
          <form onSubmit={handleSubmit}>
            <TaskPanel title={title} isVisible={isOpen} ctaButtons={ctaButtons} onCancel={handleModalClose}>
              {addPasswordResponse ? (
                <Form transparent={true}>
                  <p>{t('secretGenerated')}</p>
                  <Input
                    formLabel={t('fields.generatedSecret')}
                    value={addPasswordResponse.password.secretText ?? undefined}
                    readOnly
                    copyButton
                  />
                </Form>
              ) : (
                <Form transparent={true}>
                  <p>{t('renewalHelp')}</p>
                  <Select
                    formLabel={t('fields.expires')}
                    options={expirationOptions}
                    onChange={onExpirationChange}
                    value={selectedOption}
                    required
                  />
                  {selectedOption === '-1' && (
                    <DatePicker
                      formLabel={t('fields.end')}
                      minDate={minDate}
                      maxDate={maxDate}
                      name="endDateTime"
                      required
                    />
                  )}
                  <Input formLabel={tCommon('fields.description')} name="displayName" />
                </Form>
              )}
            </TaskPanel>
          </form>
        );
      }}
    </Formik>
  );
};

function computeExpirationDate(days: number): string {
  const expirationDate = new Date();
  expirationDate.setUTCDate(expirationDate.getUTCDate() + days);
  expirationDate.setUTCHours(24, 0, 0, 0);
  return expirationDate.toISOString();
}

export default AddPasswordPanel;
